import {
  safeClear,
  safeCreateStore,
  safeDelete,
  safeEntries,
  safeGet,
  safeSet,
} from '@canvas/idb-cache';

const KEY_SEPARATOR = ':::';

export type DurableIdbCacheValidator<D> = (key: string, value: D) => boolean;

export type DurableIdbCacheConfig<D> = {
  cacheName: string;
  validator?: DurableIdbCacheValidator<D>;
  allowAnonymous?: boolean;
};

export class DurableIdbCache<D> {
  private idbStore;
  private currentUser: string | undefined;
  private userIdbKey: string;
  private allowAnonymous?: boolean;
  private validator?: DurableIdbCacheValidator<D>;

  constructor(config: DurableIdbCacheConfig<D>) {
    const { cacheName, validator } = config;
    const dbName = `canvas-${cacheName}-db`;
    const storeName = `canvas-${cacheName}-data`;
    this.userIdbKey = `canvas-${cacheName}-user`;
    this.idbStore = safeCreateStore(dbName, storeName);
    this.validator = validator;
    this.allowAnonymous = config.allowAnonymous ?? false;
  }

  async prepare() {
    this.currentUser = await safeGet<string>(this.userIdbKey);
  }

  user() {
    return this.currentUser;
  }

  async cleanup(nextUser: string) {
    await safeClear(this.idbStore);
    await safeSet(this.userIdbKey, nextUser);

    this.currentUser = nextUser;
  }

  private isEntryValid(key: string, value: D): boolean {
    if (!this.validator) {
      return true;
    }
    return this.validator(key, value);
  }

  async entries(): Promise<[IDBValidKey, D][]> {
    const cachedEntries = (await safeEntries<D>(this.idbStore)) ?? [];
    const entries: [IDBValidKey, D][] = [];

    for (const [idbKey, value] of cachedEntries) {
      const [user, key] = this.decodeIdbKey(idbKey) ?? [];

      if (user === this.currentUser && this.isEntryValid(key, value)) {
        entries.push([key, value]);
      }
    }

    return entries;
  }

  async set(key: string, data: D) {
    if (!this.currentUser && !this.allowAnonymous) {
      return;
    }
    return safeSet(this.encodeIdbKey(key), data, this.idbStore);
  }

  async get(key: string) {
    if (!this.currentUser && !this.allowAnonymous) {
      return;
    }
    return safeGet(this.encodeIdbKey(key), { customStore: this.idbStore });
  }

  async delete(key: string) {
    if (!this.currentUser && !this.allowAnonymous) {
      return;
    }

    return safeDelete(this.encodeIdbKey(key), this.idbStore);
  }

  private encodeIdbKey(key: string) {
    return `${this.currentUser}${KEY_SEPARATOR}${key}`;
  }

  private decodeIdbKey(idbKey: IDBValidKey) {
    return `${idbKey}`.split(KEY_SEPARATOR);
  }
}
